import { useState } from "react";

function buildKey(key: string) {
  return `truth:${key}`;
}

type UseLocalStorageOptions = {
  /** If true, don't write defaultValue to localStorage on mount. */
  deferInit?: boolean;
};

function safeParse<T>(raw: string): T | string {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return raw;
  }
}

const useLocalStorage = <T>(
  keyName: string,
  defaultValue: T | null,
  options?: UseLocalStorageOptions,
) => {
  const key = buildKey(keyName);
  const defer = options?.deferInit === true;

  const [isSet, setIsSet] = useState<boolean>(() => {
    try {
      if (typeof window === "undefined") return false;
      return window.localStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  });

  const [storedValue, setStoredValue] = useState<T | null>(() => {
    try {
      if (typeof window === "undefined") return defaultValue;

      const existing = window.localStorage.getItem(key);
      if (existing !== null) {
        return safeParse<T>(existing) as T;
      }

      if (defer) {
        return defaultValue;
      }

      // Initialize storage with default
      const toSave =
        typeof defaultValue === "string"
          ? (defaultValue as unknown as string)
          : JSON.stringify(defaultValue);
      window.localStorage.setItem(key, toSave);
      return defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setValue = (newValue: T) => {
    try {
      const toSave =
        typeof newValue === "string"
          ? (newValue as unknown as string)
          : JSON.stringify(newValue);
      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, toSave);
      }
      setIsSet(true);
    } catch {
      /* empty */
    }
    setStoredValue(newValue);
  };

  return [
    storedValue as T,
    setValue as (newValue: T) => void,
    { isSet },
  ] as const;
};

export { useLocalStorage, buildKey };
